// One collapsible step card — RON-5465. Prototype region: `.section-card`.
//
// Four of these make the pack. The card carries its own state in the border and the badge, so the
// shape of the page answers "where am I up to" before a single row is read.
//
// THE `locked` STATE IS NO LONGER PRODUCED, AND IS KEPT ON PURPOSE.
//
// Steps stopped being gated on 21-October-2026 — "the contractor can work on any step in any
// order; only the Submit button requires every step to be complete" — so `sectionState` in
// `CompliancePortalUI.jsx` now only ever returns "complete" or "partial".
//
// This branch is therefore unreachable, which normally means delete it. It stays because the
// test that guards the change ("locks nothing — every step is open") asserts that no card
// carries `.portal-section--locked`. Remove the branch and that assertion can never fail again:
// the guard against the gate quietly coming back would be gone along with the gate.
//
// If the lock does return, the original reason it was dimmed rather than hidden still holds — a
// contractor who cannot find step 3 will email the host asking where it went.

import { useTranslation } from "react-i18next";

const Chevron = ({ open }) => (
  <svg
    className={`portal-section__chevron${open ? " portal-section__chevron--open" : ""}`}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const Tick = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const PortalSection = ({
  step,
  title,
  subtitle,
  icon,
  iconTone,
  state,
  isOpen,
  onToggle,
  children,
}) => {
  const { t } = useTranslation("common");

  const locked = state === "locked";
  const complete = state === "complete";
  const partial = state === "partial";

  const badgeKey = complete
    ? "react_portal_badge_complete"
    : partial
      ? "react_portal_badge_partial"
      : "react_portal_badge_locked";

  const iconModifier = complete
    ? " portal-section__icon--complete"
    : partial
      ? " portal-section__icon--partial"
      : iconTone === "purple"
        ? " portal-section__icon--purple"
        : "";

  return (
    <section
      className={`portal-section${
        complete ? " portal-section--complete" : partial ? " portal-section--partial" : ""
      }${locked ? " portal-section--locked" : ""}`}
    >
      <button
        type="button"
        className="portal-section__header"
        onClick={locked ? undefined : onToggle}
        // A locked card is not a control. Marked disabled rather than silently inert, so the
        // keyboard skips it instead of landing on something that does nothing.
        disabled={locked}
        aria-expanded={locked ? undefined : isOpen}
      >
        <span className="portal-section__left">
          <span className={`portal-section__icon${iconModifier}`} aria-hidden="true">
            {complete ? <Tick /> : icon}
          </span>
          <span>
            <span className="portal-section__title">
              {t("react_portal_step_n").replace("%n", String(step))} — {title}
            </span>
            <span className="portal-section__subtitle">{subtitle}</span>
          </span>
        </span>

        <span
          className={`portal-section__badge portal-section__badge--${
            complete ? "complete" : partial ? "partial" : "locked"
          }`}
        >
          {t(badgeKey)}
        </span>

        {!locked && <Chevron open={isOpen} />}
      </button>

      {isOpen && !locked && <div className="portal-section__body">{children}</div>}
    </section>
  );
};

export default PortalSection;
